import { createContext } from 'react';

/**
 * Locale + timezone configuration consumed by the Sidekick package's
 * formatters and renderers. Both values are required at the consumer
 * boundary; `useSidekickConfig` resolves defaults (browser TZ and
 * the active i18next language) for callers rendering outside of
 * `SidekickConfigProvider`.
 */
export type SidekickConfig = {
  timeZone: string;
  locale: string;
  /**
   * Stable identifier for the signed-in user (e.g. the profile id).
   * Used solely to scope the localStorage consent fallback key so a
   * shared browser never leaks one user's acceptance to another. Has
   * no effect once the host injects real `getConsentStatus` /
   * `acceptConsent` callbacks (the ECPP-backed path owns identity
   * server-side). Optional — omitting it falls back to an anonymous
   * key.
   */
  userId?: string;
};

/**
 * Internal context shape. `null` represents "no provider on the
 * tree"; `useSidekickConfig` interprets that as a request to resolve
 * the values from the browser + i18next layer instead.
 *
 * Exported so test-only utilities and SSR shims can read the
 * context directly; production code should always go through
 * `useSidekickConfig` to pick up the browser-fallback path.
 */
export const SidekickConfigContext = createContext<SidekickConfig | null>(null);
